import { useEffect, useState } from 'react'
import { Button } from './Button'
import { useWorkDetails } from '~/hooks/useWorkDetails'
import { useWorkVoting } from '~/hooks/useWorkVoting'
import { useAccount } from '~/hooks/useAccount'
import { MarkdownContent } from './MarkdownContent'
import { useVote } from '~/hooks/useVote'
import { useDagProgram } from '~/hooks/useDagProgram'
import { useUserWorkVote } from '~/hooks/useUserWorkVote'
import { useWorkOpen } from '~/hooks/useWorkOpen'
import { formatLargeBalance } from '~/utils/formatLargeBalance'
import toast from 'react-hot-toast'

interface WorkItemProps {
  workId: string;
  tokenAddress?: string; // Optional fallback token address
  isUserWork?: boolean; // Indicates if this work belongs to the current user
}

export const WorkItem = ({ workId, tokenAddress, isUserWork }: WorkItemProps) => {
  const { work, isLoading, error, fetchWork } = useWorkDetails({ workId })
  const { account } = useAccount()
  const { getProjectPda, getUserLockPda } = useDagProgram()
  const { voteOnWork, isLoading: isVotingAny, error: voteError } = useVote()
  const { openWork, isLoading: isOpeningWork } = useWorkOpen()
  const [activeVoteDirection, setActiveVoteDirection] = useState<'for' | 'against' | null>(null)

  // Get user's current work vote - use fallback tokenAddress if work.proposal.tokenAddress is not available
  const effectiveTokenAddress = work?.proposal?.tokenAddress || tokenAddress
  const projectPda = effectiveTokenAddress ? getProjectPda(effectiveTokenAddress) : null
  const { userWorkVote, refetchUserWorkVote } = useUserWorkVote({
    workItemPda: workId,
    proposalPda: work?.proposal?.proposalId || '',
    projectPda: projectPda!
  })

  useEffect(() => {
    fetchWork()
  }, [fetchWork])

  if (isLoading || !work) {
    return (
      <div className="p-4 rounded-lg bg-white/5 border border-solid border-[#212425]">
        <p className="text-[#9c9e9e]">Loading...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4 rounded-lg bg-white/5 border border-solid border-[#212425]">
        <p className="text-red-500">Error: {error.message}</p>
      </div>
    )
  }

  const handleVote = async (voteDirection: 'for' | 'against') => {
    if (!work || !account?.address || !effectiveTokenAddress) {
      console.error('Cannot vote: missing required data', { 
        work, 
        account: account?.address, 
        proposal: work?.proposal,
        effectiveTokenAddress 
      })
      return
    }

    setActiveVoteDirection(voteDirection)
    try {
      const projectPda = getProjectPda(effectiveTokenAddress)
      const userLockPda = getUserLockPda(account.address, projectPda)

      await voteOnWork({
        workId: work.workId,
        workItemPda: work.workId,
        proposalPda: work.proposal?.proposalId || '',
        projectPda: projectPda.toBase58(),
        userLockPda: userLockPda.toBase58(),
        voteDirection,
        tokenAddress: effectiveTokenAddress
      })

      await fetchWork()
      await refetchUserWorkVote()
    } catch (err) {
      console.error('Failed to vote on work:', err)
      throw err
    } finally {
      setActiveVoteDirection(null)
    }
  }

  const handleOpenForReview = async () => {
    if (!account?.address) {
      toast.error('Please connect your wallet')
      return
    }

    try {
      console.log('Opening work for review:', workId)
      await openWork(workId)
      console.log('Work opened successfully, refreshing data...')
      await fetchWork() // Refresh the work data to show updated status
      console.log('Work data refreshed, new status:', work?.status)
    } catch (error) {
      console.error('Failed to open work for review:', error)
      // Error is already handled by the hook
    }
  }

  const isVoting = (direction: 'for' | 'against') => {
    return activeVoteDirection === direction
  }

  const isDisabled = () => {
    return !account?.address || isVotingAny || userWorkVote !== null || !effectiveTokenAddress
  }

  // Check if current user is the creator of this work
  const isCreator = isUserWork || account?.address?.toLowerCase() === work.worker.solanaWalletAddress?.toLowerCase()

  // Debug logging
  console.log('WorkItem Debug:', {
    workId,
    workStatus: work.status,
    workCreator: work.worker.solanaWalletAddress,
    currentUser: account?.address,
    isCreator,
    isDraft: work.status.toLowerCase() === "draft",
    shouldShowButton: work.status.toLowerCase() === "draft" && isCreator
  })

  return (
    <div className="flex flex-col gap-4 p-4 rounded-lg bg-white/5 border border-solid border-[#212425]">
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <h3 className="text-lg font-medium text-white">{work.title}</h3>
          <p className="text-sm text-[#9c9e9e]">
            by {work.worker.githubId || work.worker.solanaWalletAddress}
          </p>
        </div>
        {work.githubPullRequestUrl && (
          <a
            href={work.githubPullRequestUrl}
            target="_blank"
            rel="noreferrer"
            className="flex justify-center items-center relative gap-1 px-3 py-2 rounded bg-[#212425]"
          >
            <svg
              width={16}
              height={17}
              viewBox="0 0 16 17"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="w-4 h-4 relative"
              preserveAspectRatio="none"
            >
              <path
                d="M6.8679 9.06611C7.11101 9.39112 7.42118 9.66004 7.77736 9.85464C8.13354 10.0492 8.52741 10.165 8.93225 10.194C9.33709 10.223 9.74343 10.1645 10.1237 10.0227C10.504 9.88083 10.8493 9.65885 11.1363 9.3718L12.8345 7.67351C13.3501 7.13968 13.6354 6.4247 13.629 5.68256C13.6225 4.94042 13.3249 4.2305 12.8001 3.7057C12.2753 3.18091 11.5653 2.88323 10.8232 2.87679C10.0811 2.87034 9.36608 3.15563 8.83225 3.67123L7.85856 4.63925M9.13228 7.93392C8.88917 7.60891 8.579 7.33998 8.22282 7.14538C7.86664 6.95078 7.47277 6.83506 7.06793 6.80607C6.66309 6.77707 6.25675 6.83549 5.87647 6.97734C5.49619 7.1192 5.15087 7.34118 4.86392 7.62823L3.16564 9.32651C2.65005 9.86034 2.36475 10.5753 2.3712 11.3175C2.37765 12.0596 2.67533 12.7695 3.20012 13.2943C3.72491 13.8191 4.43483 14.1168 5.17697 14.1232C5.91911 14.1297 6.6341 13.8444 7.16793 13.3288L8.13595 12.3608"
                stroke="white"
                strokeWidth="1.33333"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <p className="text-[13px] font-semibold text-white">Github</p>
          </a>
        )}
      </div>

      <MarkdownContent 
        content={work.description}
        className="p-4 rounded-lg bg-[#212425]/50"
      />

      {/* Status Badge */}
      <div className="mb-4">
        <span
          className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
            work.status.toLowerCase() === "voting" || work.status.toLowerCase() === "open_for_voting"
              ? "bg-yellow-900/20 text-yellow-400 border border-yellow-500/50"
              : work.status.toLowerCase() === "active" || work.status.toLowerCase() === "approved"
              ? "bg-green-900/20 text-green-400 border border-green-500/50"
              : work.status.toLowerCase() === "rejected"
              ? "bg-red-900/20 text-red-400 border border-red-500/50"
              : work.status.toLowerCase() === "draft"
              ? "bg-gray-900/20 text-gray-400 border border-gray-500/50"
              : "bg-blue-900/20 text-blue-400 border border-blue-500/50"
          }`}
        >
          {work.status.charAt(0).toUpperCase() + work.status.slice(1)}
        </span>
      </div>

      {/* Ready for Review Button - only show for draft work by the creator */}
      {(work.status.toLowerCase() === "draft") && isCreator && (
        <div className="p-3 rounded-lg bg-yellow-900/20 border border-yellow-500/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg
                width={16}
                height={16}
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4 text-yellow-400"
              >
                <path
                  d="M8 1L10.09 5.26L15 6L11 9.74L11.91 14.74L8 12.77L4.09 14.74L5 9.74L1 6L5.91 5.26L8 1Z"
                  fill="currentColor"
                />
              </svg>
              <span className="text-sm font-medium text-yellow-400">
                Ready to submit for review?
              </span>
            </div>
            <Button
              onClick={handleOpenForReview}
              disabled={!account?.address || isOpeningWork}
              loading={isOpeningWork}
              size="sm"
              type="primary"
            >
              Ready for Review
            </Button>
          </div>
        </div>
      )}

      {/* Current User Vote Display */}
      {account?.address && userWorkVote && (
        <div className="mt-3 p-3 rounded-lg bg-blue-900/20 border border-blue-500/50">
          <div className="flex items-center gap-2">
            <svg
              width={16}
              height={16}
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="w-4 h-4 text-blue-400"
            >
              <path
                d="M8 1L10.09 5.26L15 6L11 9.74L11.91 14.74L8 12.77L4.09 14.74L5 9.74L1 6L5.91 5.26L8 1Z"
                fill="currentColor"
              />
            </svg>
            <span className="text-sm font-medium text-blue-400">
              You voted {userWorkVote.voteDirection === 'for' ? 'for' : 'against'} this work
            </span>
            <span className="text-xs text-blue-300">
              (Power: {formatLargeBalance(userWorkVote.votePower)})
            </span>
          </div>
        </div>
      )}

      {/* Voting Section */}
      {(work.status.toLowerCase() === "voting" || work.status.toLowerCase() === "open_for_voting") && (
        <div className="flex flex-col gap-4">
          <div className="p-2 rounded bg-green-900/20 border border-green-500/50">
            <div className="text-xs text-green-300">
              Debug: Voting section is showing! Status: "{work.status}", Votes: {work.votesFor} / {work.votesAgainst}
            </div>
          </div>
          <div className="flex items-center gap-4 mb-4">
            <div className="flex-1">
              <div className="h-2 rounded-full bg-[#212425] overflow-hidden">
                <div
                  className="h-full bg-green-500 transition-all"
                  style={{
                    width: `${
                      work.votesFor + work.votesAgainst > 0
                        ? (work.votesFor / (work.votesFor + work.votesAgainst)) *
                          100
                        : 0
                    }%`,
                  }}
                />
              </div>
            </div>
            <div className="text-white">
              {work.votesFor} / {work.votesAgainst}
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => handleVote("for")}
              disabled={isDisabled()}
              loading={isVoting("for")}
              size="sm"
              type="secondary"
              background="#212425"
              color="white"
              className={`hover:bg-[#60e25e] hover:text-[#19191b] transition-colors ${
                userWorkVote?.voteDirection === 'for' ? 'opacity-75' : ''
              }`}
            >
              {userWorkVote?.voteDirection === 'for' ? 'Voted For' : 'Vote For'}
            </Button>
            <Button
              onClick={() => handleVote("against")}
              disabled={isDisabled()}
              loading={isVoting("against")}
              size="sm"
              type="secondary"
              background="#212425"
              color="white"
              className={`hover:bg-[#ff4949] hover:text-[#19191b] transition-colors ${
                userWorkVote?.voteDirection === 'against' ? 'opacity-75' : ''
              }`}
            >
              {userWorkVote?.voteDirection === 'against' ? 'Voted Against' : 'Vote Against'}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}